import React, { useMemo, useState, useEffect } from 'react';
import SectionCard from '../components/SectionCard.jsx';
import NestedSection from '../components/NestedSection.jsx';
import KPICard from '../components/KPICard.jsx';
import DataTable from '../components/DataTable.jsx';
import MoneyCell from '../components/MoneyCell.jsx';
import AssetTransactionModal from '../components/AssetTransactionModal.jsx';
import JournalEntryForm from '../components/JournalEntryForm.jsx';
import ContextPanel from '../components/ContextPanel.jsx';
// Phase 1.5 — vendor-specific reconciliation banner removed in Agnostic Purification.
import FormalStatementTable, {
  shapeIncomeStatement, shapeCashFlowStatement,
  shapeIncomeStatementMulti, shapeBalanceSheetMulti, shapeCashFlowStatementMulti,
} from '../components/FormalStatementTable.jsx';
import FormalStatementSection from '../components/FormalStatementSection.jsx';
import { makeShowBlock, SubViewBreadcrumb } from '../components/SubViewShell.jsx';
import EmptyState from '../components/EmptyState.jsx';
import ComparativeTablesView from '../components/ComparativeTablesView.jsx';
import ModuleSettingsPanel from '../components/ModuleSettingsPanel.jsx';
import ModifiedBadge, {
  isAssetPinned, isLTLPinned, countAssetPins, countLTLPins, OVERRIDE_REASON,
} from '../components/ModifiedBadge.jsx';
import { useAppConfirm } from '../components/AppDialog.jsx';
import { FIXED_ASSETS, LONG_TERM_LIABILITIES, CASH_BALANCES, CC_LIABILITIES } from '../data.js';
import { todayISO } from '../formatting.js';
import {
  fmtMoney, fmtPct, currentYM,
  applyAdjustingEntries,
  buildIncomeStatement, buildCashFlowSnapshot,
  buildThreeStatementForDirection, getTabDirection,
  aggregateByDensity, fmtPeriod, normalizePeriodScalar,
  currentPeriodStateForRows,
  buildSimulationsRegistry, summarizeSimulationsRegistry,
  getModuleFeatures,
} from '../engine.js';

const TAB_CONTENT = 'flex flex-col [&>*+*]:mt-4';
const KPI_ROW = 'grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 mb-5';
const PM_BTN = 'bg-transparent border border-border-strong text-text-muted px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:text-text hover:border-accent-gold';
const PM_BTN_PRIMARY = 'bg-[rgba(240,180,41,0.10)] border border-accent-gold text-accent-gold px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:bg-[rgba(240,180,41,0.18)]';
const BTN_GHOST_SM = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-2 py-[2px] rounded-sm font-[inherit] text-[11px] cursor-pointer hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)]';
const COMPACT_LIST = 'mt-[0.6rem] mb-0 pl-[1.1rem] text-text-muted text-[0.84rem] leading-[1.65]';
const HINT_INLINE = 'inline-block px-1 text-[0.85em] text-text-muted opacity-70 not-italic';
/* Just vertical spacing between the 3 statement tables — each table already
   has its own outlined border from FormalStatementTable, so no divider needed.
   Matches the visual treatment of statements in Actuals/Budgets/Forecasts/Synthesis tabs. */
const FCC_3STMT = '[&>*+*]:mt-5';

/* JE list — visual chrome shared with the legacy Actuals JE view.
   Constants kept local to FCC since this is now the only home for JE
   rendering (Proposal 3, 2026-04-25). */
const BADGE_BASE = 'inline-block text-[0.6rem] font-bold px-[6px] py-px rounded-[3px] uppercase tracking-[0.06em] ml-[0.4rem] align-middle border';
const BADGE_POSTED = `${BADGE_BASE} bg-[rgba(240,180,41,0.14)] text-accent-gold border-[rgba(240,180,41,0.35)]`;
const BADGE_STOPPED = `${BADGE_BASE} bg-[rgba(140,148,167,0.14)] text-text-dim border-border`;
const BADGE_FORECAST = `${BADGE_BASE} bg-[rgba(96,165,250,0.18)] text-accent-blue border-[rgba(96,165,250,0.35)]`;
const HINT_JE = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const PM_BTN_JE = 'bg-transparent border border-border-strong text-text-muted px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:text-text hover:border-accent-gold disabled:opacity-50 disabled:cursor-not-allowed';
const PM_BTN_DANGER_JE = `${PM_BTN_JE} hover:!text-accent-red hover:!border-accent-red`;

export default function FCCTab({ scenario, setScenario, onJump, activeSection, onBackToOverview }) {
  const show = makeShowBlock(activeSection);
  const { requestConfirmation, dialog: confirmDialog } = useAppConfirm();
  /* v8 Path E — scroll-to-section when sidebar routes to a sub-item. */
  useEffect(() => {
    if (!activeSection || typeof document === 'undefined') return;
    const t = setTimeout(() => {
      const el = document.getElementById(`section-${activeSection}`) || document.getElementById(activeSection);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.remove('section-focused');
      void el.offsetWidth;
      el.classList.add('section-focused');
      setTimeout(() => el.classList.remove('section-focused'), 1900);
    }, 60);
    return () => clearTimeout(t);
  }, [activeSection]);
  const [modal, setModal] = useState(null);
  /* Proposal 3 (2026-04-25) — Journal Entry editor moved from Actuals to FCC.
     JEs are simulation primitives — they belong in the FCC sandbox alongside
     buy/sell + LTL sims. Engine still applies posted JEs across all modules. */
  const [showJEForm, setShowJEForm] = useState(false);
  const adjEntries = scenario.adjustingEntries ?? [];
  const postedCount = adjEntries.filter((e) => e.status === 'posted').length;
  const draftCount  = adjEntries.filter((e) => e.status === 'draft').length;

  const pushEntry = (entry) => {
    setScenario({
      ...scenario,
      adjustingEntries: [...(scenario.adjustingEntries ?? []), entry],
    });
    setShowJEForm(false);
  };
  const reverseEntry = (entry) => {
    const reverse = {
      id: `adj-rev-${Date.now().toString(36)}`,
      date: todayISO(),
      memo: `Reverse of ${entry.id}: ${entry.memo}`,
      lines: entry.lines.map((l) => ({ account: l.account, debit: Number(l.credit) || 0, credit: Number(l.debit) || 0 })),
      status: 'posted',
      affects: entry.affects,
      postedAt: new Date().toISOString(),
      reverses: entry.id,
    };
    pushEntry(reverse);
  };
  const deleteDraft = async (id) => {
    const ok = await requestConfirmation({
      title: 'Delete draft entry?',
      message: 'This removes the draft adjusting entry from the current scenario.',
      confirmLabel: 'Delete draft',
      tone: 'danger',
    });
    if (!ok) return;
    setScenario((current) => ({
      ...current,
      adjustingEntries: (current.adjustingEntries ?? []).filter((e) => e.id !== id),
    }));
  };

  const { resolvedAssets, resolvedLTLs, resolvedCash, resolvedCC } = useMemo(() => {
    const assets = [...FIXED_ASSETS.map(a => ({ ...a }))];
    const ltls   = [...LONG_TERM_LIABILITIES.map(l => ({ ...l }))];
    const cash   = [...CASH_BALANCES.map(c => ({ ...c }))];
    const cc     = [...CC_LIABILITIES.map(c => ({ ...c }))];
    const txns   = scenario.assetTransactions ?? [];

    for (const t of txns) {
      if (t.mode === 'sell') {
        const i = assets.findIndex(a => a.id === t.assetId);
        if (i >= 0) { assets.splice(i, 1); }
        if (t.linkedLTLId) {
          const li = ltls.findIndex(l => l.id === t.linkedLTLId);
          if (li >= 0) {
            const ltl = ltls[li];
            const payoff = Math.min(ltl.outstanding, t.ltlPayoff ?? ltl.outstanding);
            ltl.outstanding = Math.max(0, ltl.outstanding - payoff);
            if (ltl.outstanding <= 0) { ltls.splice(li, 1); }
          }
        }
        const mari = cash.find(c => c.id === 'bank-mari');
        if (mari) mari.balance = mari.balance + (t.netCash ?? 0);
      } else if (t.mode === 'buy') {
        assets.push({
          id: `sim-${t.id}`,
          name: t.assetName,
          category: t.category || 'Other',
          costBasis: t.purchasePrice,
          currentValue: t.purchasePrice,
          accumDepreciation: 0,
          liabilityLink: t.createLTL ? `ltl-sim-${t.id}` : null,
          purchaseDate: t.closingDate,
          status: 'held-simulated',
          note: `Simulated purchase (${t.id})`,
        });
        if (t.createLTL) {
          ltls.push({
            id: `ltl-sim-${t.id}`,
            name: `LTL: ${t.assetName}`,
            outstanding: t.purchasePrice - (t.downPayment ?? 0),
            monthlyPayment: t.monthlyPayment ?? 0,
            interestRate: t.interestRate ?? 0.075,
            amortizeMonths: t.amortizeMonths ?? 240,
            linkedAsset: `sim-${t.id}`,
            note: 'Simulated financing',
          });
        }
        const mari = cash.find(c => c.id === 'bank-mari');
        if (mari) mari.balance = Math.max(0, mari.balance - (t.downPayment ?? 0));
      }
    }

    return { resolvedAssets: assets, resolvedLTLs: ltls, resolvedCash: cash, resolvedCC: cc };
  }, [scenario.assetTransactions]);

  const totalLiquid = resolvedCash.filter(c => c.liquid).reduce((s, c) => s + c.balance, 0);
  const totalDust   = resolvedCash.filter(c => !c.liquid).reduce((s, c) => s + c.balance, 0);
  const totalCCDebt = resolvedCC.filter(c => c.balance > 0).reduce((s, c) => s + c.balance, 0);
  const totalCCCredits = resolvedCC.filter(c => c.balance < 0).reduce((s, c) => s + Math.abs(c.balance), 0);
  const totalFixedBook = resolvedAssets.reduce((s, a) => s + Math.max(0, a.currentValue - a.accumDepreciation), 0);
  const totalLTL    = resolvedLTLs.reduce((s, l) => s + l.outstanding, 0);

  const totalAssets = totalLiquid + totalDust + totalFixedBook;
  const totalLiab   = totalCCDebt + totalLTL;
  const netWorth    = totalAssets - totalLiab;
  const debtCoverage = totalCCDebt > 0 ? totalLiquid / totalCCDebt : Infinity;

  const adj = scenario.includeAdjustments !== false
    ? applyAdjustingEntries(scenario)
    : { byAccount: {}, totals: { is: 0, bs: 0 }, count: 0, entries: [] };

  const bsLiabAdj = Object.entries(adj.byAccount)
    .filter(([k]) => {
      const lower = k.toLowerCase();
      return lower.includes('payable') || lower.includes('liabilit') || lower.includes('accrued');
    })
    .reduce((s, [, v]) => s + v, 0);
  const bsAssetAdj = Object.entries(adj.byAccount)
    .filter(([k]) => {
      const lower = k.toLowerCase();
      return lower.includes('asset') || lower.includes('receivable') || lower.includes('cash') || lower.includes('bank');
    })
    .reduce((s, [, v]) => s + v, 0);

  const adjTotalAssets = totalAssets + bsAssetAdj;
  const adjTotalLiab   = totalLiab + bsLiabAdj;
  const adjNetWorth    = adjTotalAssets - adjTotalLiab;

  const fccPeriods = normalizePeriodScalar(scenario.formalPeriodCount, 12);
  const fccDensity = normalizePeriodScalar(scenario.formalPeriodDensity, 1);
  const fccHorizon = fccPeriods * fccDensity;
  const fccDirection = getTabDirection(scenario, 'fcc');
  const fccProjection = useMemo(() => {
    const raw = buildThreeStatementForDirection(scenario, fccDirection, fccHorizon, fccDensity);
    const forecast         = aggregateByDensity(raw.forecast,         fccDensity, 'last');
    const incomeStatements = aggregateByDensity(raw.incomeStatements, fccDensity, 'sum');
    const balanceSheets    = aggregateByDensity(raw.balanceSheets,    fccDensity, 'last');
    const cashFlows        = aggregateByDensity(raw.cashFlows,        fccDensity, 'sum');
    const periodLabels     = forecast.map(r => fmtPeriod(r.ym ?? r.label, fccDensity));
    const currentState     = currentPeriodStateForRows(forecast, fccDensity);
    const currentLabel     = currentState.label;
    return { forecast, incomeStatements, balanceSheets, cashFlows, periodLabels, currentLabel, currentState };
  }, [scenario, fccHorizon, fccDensity, fccDirection]);
  const { incomeStatements: fccIS, balanceSheets: fccBS, cashFlows: fccCF, forecast: fccForecast, periodLabels: fccPeriodLabels } = fccProjection;
  const fccCurrentLabel = fccProjection.currentLabel;
  const fccCurrentProof = fccProjection.currentState;

  const moneyFmt = (v) => typeof v === 'number' ? <MoneyCell value={v} decimals={2} scenario={scenario} /> : v;

  const removeTxn = async (id) => {
    const ok = await requestConfirmation({
      title: 'Remove this simulated transaction?',
      message: 'This removes the Command Module-owned simulation from the current scenario.',
      confirmLabel: 'Remove simulation',
      tone: 'danger',
    });
    if (!ok) return;
    setScenario((current) => ({
      ...current,
      assetTransactions: (current.assetTransactions ?? []).filter(t => t.id !== id),
    }));
  };

  const txns = scenario.assetTransactions ?? [];

  if (activeSection === 'comparative') {
    return (
      <div className={TAB_CONTENT}>
        <SubViewBreadcrumb moduleId="fcc" activeSection={activeSection} onBack={onBackToOverview} />
        <ComparativeTablesView scenario={scenario} moduleId="fcc" />
        {confirmDialog}
      </div>
    );
  }

  const features = getModuleFeatures(scenario, 'fcc');

  return (
    <div className={TAB_CONTENT}>
      <SubViewBreadcrumb moduleId="fcc" activeSection={activeSection} onBack={onBackToOverview} />
      <div className="inline-flex items-center gap-2 text-accent-gold text-[0.78rem] tracking-[0.08em] mb-[0.75rem]">
        <span className="text-[0.95rem]">⟡</span>
        <span className="uppercase">Command Module — what you own · what you owe · what's possible</span>
      </div>

      {show('overview') && (
        <ModuleSettingsPanel
          scenario={scenario}
          setScenario={setScenario}
          moduleId="fcc"
          title="Command Module · module settings"
          features={[
            { key: 'liquid',         label: 'Liquid position',         hint: 'Bank + cash with tier rollup' },
            { key: 'fixedAssets',    label: 'NC-Assets & NC-Liabilities', hint: 'Long-lived assets + long-term obligations + buy/sell/payoff actions' },
            { key: 'journalEntries', label: 'Journal entries',         hint: 'Post/draft adjusting JEs (moved from Actuals — JEs are simulations)' },
            { key: 'transactions',   label: 'Simulated transactions',  hint: 'Central audit ledger (every module\u2019s overrides)' },
            { key: 'threeStmt',      label: '3-Statement Model',       hint: 'IS / BS / CF × horizon' },
          ]}
        />
      )}

      {/* Liquid Position */}
      {show('overview', 'liquid') && features.liquid && (
      <SectionCard
        sectionId="liquid"
        title="Liquid Position"
        subtitle="Cash + e-wallets from imported ledger snapshot state. Dust / maintaining-balance excluded from Liquid."
      >
        <div className={KPI_ROW}>
          <KPICard
            label="Total Liquid"
            value={<MoneyCell value={totalLiquid} decimals={2} tone="good" term="liquidAssets" registryKey="liquidAssets" onJump={onJump} scenario={scenario} label="Total Liquid (Command Module)" />}
            sub={`${resolvedCash.filter(c => c.liquid).length} liquid accounts`}
            tone="good"
            provenance="computed"
            provenanceTip="Ledger CASH_BALANCES + any sandbox simulations applied (asset buy/sell adjustments to cash). Strip down to plain ledger by clearing Simulated transactions."
          />
          <KPICard
            label="Total Short-term Debt"
            value={<MoneyCell value={totalCCDebt} decimals={2} tone="bad" term="ccDebt" registryKey="ccDebt" onJump={onJump} scenario={scenario} label="Total Short-term Debt (Command Module)" />}
            sub={`${resolvedCC.filter(c => c.balance > 0).length} revolving lines owing`}
            tone="bad"
            provenance="ledger"
            provenanceTip="Sum of positive short-term financing balances. Sandbox sims that create new long-term liabilities do not affect this row."
          />
          <KPICard
            label="Net Worth (liquid)"
            value={<MoneyCell value={totalLiquid - totalCCDebt} decimals={2} tone={(totalLiquid - totalCCDebt) >= 0 ? 'good' : 'bad'} term="netWorth" scenario={scenario} label="Net Worth (liquid only)" />}
            sub="Liquid - short-term debt"
            provenance="computed"
            provenanceTip="Total Liquid minus total short-term debt. Excludes fixed assets and long-term liabilities - the cash-only net-worth view."
          />
          <KPICard
            label="Debt Coverage"
            value={debtCoverage === Infinity ? '∞' : fmtPct(debtCoverage, 1)}
            sub="Liquid / short-term debt - below 1.0 means cash cannot cover it"
            tone={debtCoverage >= 0.1 ? 'good' : 'bad'}
            provenance="computed"
            provenanceTip="Total Liquid divided by total short-term debt. Above 1.0 means the cash position covers the current financing stack."
          />
        </div>

        <NestedSection
          sectionId="fcc-liquid-detail"
          title="Cash accounts — detail"
          subtitle={`${resolvedCash.length} accounts · liquid ${fmtMoney(totalLiquid, { decimals: 0 })} · maintaining/foreign ${fmtMoney(totalDust, { decimals: 0 })}`}
          scenario={scenario} setScenario={setScenario}
          headline={null}
          fullCount={resolvedCash.length}
          fullList={
            <DataTable
              compact
              columns={[
                { key: 'name',    label: 'Account', width: '50%' },
                { key: 'type',    label: 'Type',    width: '14%' },
                { key: 'balance', label: 'Balance', align: 'right', width: '22%', format: moneyFmt },
                { key: 'note',    label: 'Note' },
              ]}
              rows={[...resolvedCash].sort((a, b) => b.balance - a.balance)}
              footer={{ name: 'Total (all)', type: '', balance: totalLiquid + totalDust, note: '' }}
            />
          }
        />

        <NestedSection
          sectionId="fcc-cc-detail"
          title="Short-term financing — detail"
          subtitle={`${resolvedCC.length} lines · debt ${fmtMoney(totalCCDebt, { decimals: 0 })} · credits ${fmtMoney(totalCCCredits, { decimals: 0 })}`}
          scenario={scenario} setScenario={setScenario}
          headline={null}
          fullCount={resolvedCC.length}
          fullList={
            <DataTable
              compact
              columns={[
                { key: 'card',    label: 'Line', width: '44%' },
                { key: 'balance', label: 'Balance', align: 'right', width: '16%',
                  format: (v) => <MoneyCell value={v} decimals={2} tone={v > 0 ? 'bad' : 'good'} scenario={scenario} />,
                },
                { key: 'limit',   label: 'Limit', align: 'right', width: '14%',
                  format: (v) => v == null ? '—' : fmtMoney(v, { decimals: 0 }),
                },
                { key: 'note',    label: 'Note' },
              ]}
              rows={[...resolvedCC].sort((a, b) => b.balance - a.balance)}
              footer={{ card: 'Net CC exposure', balance: totalCCDebt - totalCCCredits, limit: '', note: '' }}
            />
          }
        />
      </SectionCard>
      )}

      {/* NC-Assets */}
      {show('overview', 'nc-assets-liabilities') && features.fixedAssets && (() => {
      /* v8 Path AL — section-level pin summary. When any asset has
         piecewise value-pins set, append "· ● N pinned" to the subtitle
         so the user sees at-a-glance that forecast valuations deviate
         from the baseline currentValue field. */
      const pinnedCount = countAssetPins(scenario);
      return (
      <SectionCard
        sectionId="nc-assets-liabilities"
        title="NC-Assets"
        subtitle={`Long-lived assets: property, equipment, vehicles, intangibles, and other non-current assets. Sell to unlock liquidity; buy to simulate acquisition.${pinnedCount > 0 ? ` · ● ${pinnedCount} pinned for horizon` : ''}`}
        right={
          <button
            type="button"
            className={PM_BTN_PRIMARY}
            onClick={() => setModal({ mode: 'buy' })}
          >
            + Buy
          </button>
        }
      >
        <DataTable
          columns={[
            { key: 'name',       label: 'Asset', width: '26%' },
            { key: 'category',   label: 'Class', width: '12%' },
            { key: 'costBasis',  label: 'Cost basis', align: 'right', width: '13%', format: moneyFmt },
            { key: 'currentValue', label: 'Current value', align: 'right', width: '13%',
              /* v8 Path AL — inline pin badge. When this asset has any
                 piecewise value pins, append a gold dot next to the
                 current value so users see the horizon override at
                 a glance (without hiding the baseline number). */
              format: (v, row) => {
                const pinned = isAssetPinned(scenario, row.id);
                return (
                  <span className="inline-flex items-center gap-[4px]">
                    {moneyFmt(v)}
                    {pinned && <ModifiedBadge active reason={OVERRIDE_REASON.assetPin} compact />}
                  </span>
                );
              },
            },
            { key: 'accumDepreciation', label: 'Depreciation', align: 'right', width: '11%', format: moneyFmt },
            { key: 'netBookValue', label: 'Net book', align: 'right', width: '12%',
              format: (v, row) => moneyFmt(row.currentValue - row.accumDepreciation),
            },
            { key: 'status',     label: 'Status', width: '10%' },
            { key: 'actions',    label: '',
              format: (_, row) => (
                row.status === 'held' || row.status === 'held-simulated'
                  ? <button type="button" className={PM_BTN} onClick={() => setModal({ mode: 'sell', asset: row, ltl: resolvedLTLs.find(l => l.id === row.liabilityLink) })}>Sell…</button>
                  : <em className={HINT_INLINE}>—</em>
              ),
            },
          ]}
          rows={resolvedAssets}
          footer={{ name: 'Total NBV', category: '', costBasis: resolvedAssets.reduce((s, a) => s + a.costBasis, 0), currentValue: resolvedAssets.reduce((s, a) => s + a.currentValue, 0), accumDepreciation: resolvedAssets.reduce((s, a) => s + a.accumDepreciation, 0), netBookValue: totalFixedBook, status: '', actions: '' }}
        />
      </SectionCard>
      );
      })()}

      {/* NC-Liabilities */}
      {show('overview', 'nc-assets-liabilities') && features.fixedAssets && (() => {
      const pinnedLTLCount = countLTLPins(scenario);
      return (
      <SectionCard
        title="NC-Liabilities"
        subtitle={`Long-term obligations and simulated non-current liabilities. Pay off early or add a new liability — all session-scoped until saved as a Model Profile.${pinnedLTLCount > 0 ? ` · ● ${pinnedLTLCount} pinned for horizon` : ''}`}
        right={
          <div className="flex gap-2">
            <button
              type="button"
              className={PM_BTN_PRIMARY}
              onClick={() => setModal({ mode: 'ltl-new' })}
              title="Simulate taking on a new long-term liability"
            >
              + Add LTL
            </button>
          </div>
        }
      >
        <DataTable
          columns={[
            { key: 'name',           label: 'Liability', width: '30%' },
            { key: 'outstanding',    label: 'Outstanding', align: 'right', width: '14%',
              /* v8 Path AL — LTL pin badge mirrors asset pin badge. */
              format: (v, row) => {
                const pinned = isLTLPinned(scenario, row.id);
                return (
                  <span className="inline-flex items-center gap-[4px]">
                    {moneyFmt(v)}
                    {pinned && <ModifiedBadge active reason={OVERRIDE_REASON.ltlPin} compact />}
                  </span>
                );
              },
            },
            { key: 'monthlyPayment', label: 'Monthly', align: 'right', width: '11%', format: moneyFmt },
            { key: 'interestRate',   label: 'Rate', align: 'right', width: '8%', format: (v) => fmtPct(v) },
            { key: 'monthsLeft',     label: 'Mo. left', align: 'right', width: '9%',
              format: (_, row) => row.monthlyPayment > 0 ? Math.round(row.outstanding / row.monthlyPayment) : '—',
            },
            { key: 'linkedAsset',    label: 'Linked asset', width: '18%' },
            { key: 'actions',        label: '',
              format: (_, row) => (
                row.outstanding > 0 ? (
                  <button
                    type="button"
                    className={PM_BTN}
                    onClick={() => setModal({ mode: 'ltl-payoff', ltl: row })}
                    title="Simulate paying off this LTL in full"
                  >
                    Pay off…
                  </button>
                ) : <em className="text-text-dim">settled</em>
              ),
            },
          ]}
          rows={resolvedLTLs}
          footer={{ name: 'Total LTL', outstanding: totalLTL, monthlyPayment: resolvedLTLs.reduce((s, l) => s + l.monthlyPayment, 0), interestRate: '', monthsLeft: '', linkedAsset: '', actions: '' }}
        />
        <p className="text-[0.72rem] text-text-dim italic mt-2 mb-0">
          NC-Liability simulations post into the same <strong className="text-accent-gold">Simulated transactions</strong> ledger as NC-Asset buy/sell — one place to audit every what-if. Save a Model Profile to keep them across reloads.
        </p>
      </SectionCard>
      );
      })()}

      {/* Balance Sheet Summary */}
      {show('overview') && (
      <SectionCard
        title="Balance Sheet — formal summary"
        subtitle={`Net worth ${adjNetWorth >= 0 ? 'positive' : 'NEGATIVE'}: Total Assets − Total Liabilities${adj.count > 0 ? ` · Includes ${adj.count} adjusting ${adj.count === 1 ? 'entry' : 'entries'}` : ''}`}
      >
        <FormalStatementTable
          entity="Current Lens Entity"
          title="Statement of Financial Position (Command Module)"
          period={`Post simulations · ${txns.length} applied${adj.count > 0 ? ` · ${adj.count} JE adjustments` : ''}`}
          sections={[
            { heading: 'Assets', lines: [
              { label: 'Liquid cash + e-wallets',    amount: totalLiquid, indent: 1 },
              { label: 'Maintaining / foreign dust', amount: totalDust,   indent: 1 },
              { label: 'NC-Assets (net book)',       amount: totalFixedBook, indent: 1 },
              ...(adj.count > 0 && bsAssetAdj !== 0 ? [{ label: `JE adjustments (${adj.count})`, amount: bsAssetAdj, indent: 1 }] : []),
            ], total: { label: 'Total Assets', amount: adjTotalAssets }},
            { heading: 'Liabilities', lines: [
              { label: 'Credit card debt',      amount: totalCCDebt, indent: 1 },
              { label: 'Long-term liabilities', amount: totalLTL,    indent: 1 },
              ...(adj.count > 0 && bsLiabAdj !== 0 ? [{ label: `JE adjustments (${adj.count})`, amount: bsLiabAdj, indent: 1 }] : []),
            ], total: { label: 'Total Liabilities', amount: adjTotalLiab }},
            { heading: 'Equity', lines: [
              { label: 'Net Worth (Assets − Liabilities)', amount: adjNetWorth, indent: 1 },
            ], total: { label: 'Total Equity', amount: adjNetWorth }},
            { lines: [], total: { label: 'Total L + E', amount: adjTotalLiab + adjNetWorth, rule: true } },
          ]}
        />
      </SectionCard>
      )}

      {show('overview') && (() => {
        const fccISsingle = buildIncomeStatement(scenario);
        return (
          <>
            <FormalStatementSection
              tabId="fcc" stmtKey="is"
              label="Income Statement (formal)"
              subtitle={`Accrual basis · Net income: ${fmtMoney(fccISsingle.netIncome, { decimals: 0 })}`}
              title="Statement of Income — Accrual Basis"
              entity="Current Lens Entity"
              period={`For the month ending ${currentYM()}-30`}
              sections={shapeIncomeStatement(fccISsingle)}
              scenario={scenario} setScenario={setScenario}
            />
            <FormalStatementSection
              tabId="fcc" stmtKey="cf"
              label="Cash Flow Statement (formal)"
              subtitle="CFO + CFI + CFF = Net change in cash · single-month snapshot"
              title="Statement of Cash Flows — Indirect Method"
              entity="Current Lens Entity"
              period={`For the month ending ${currentYM()}-30`}
              sections={shapeCashFlowStatement(buildCashFlowSnapshot(scenario, currentYM()))}
              scenario={scenario} setScenario={setScenario}
            />
          </>
        );
      })()}

      {/* 3-Statement Financial Model */}
      {show('overview', 'three-stmt') && features.threeStmt && (
      <SectionCard
        sectionId="three-stmt"
        title="3-Statement Financial Model"
        subtitle={`${fccPeriods}-period projection · IS + BS + CF · Same engine as Forecasts`}
      >
        <div className={FCC_3STMT}>
          <FormalStatementTable
            title="Projected Income Statement"
            entity="Current Lens Entity"
            period={`${fccPeriodLabels[0]} – ${fccPeriodLabels[fccPeriodLabels.length - 1]}`}
            periods={fccPeriodLabels}
            sections={shapeIncomeStatementMulti(fccIS)}
            sparklineGradient={scenario?.sparklineGradient ?? false}
            showTrendColumn={scenario?.showTrendColumn !== false}
            showRowSparkline={scenario?.showRowSparkline !== false}
            semanticTone={!!scenario?.sparklineSemanticTone}
            guides={scenario?.guides}
            hideEmptyLines
            currentPeriodLabel={fccCurrentLabel}
            currentPeriodProof={fccCurrentProof}
          />
          <FormalStatementTable
            title="Projected Balance Sheet"
            entity="Current Lens Entity"
            period={`${fccPeriodLabels[0]} – ${fccPeriodLabels[fccPeriodLabels.length - 1]}`}
            periods={fccPeriodLabels}
            sections={shapeBalanceSheetMulti(fccBS)}
            sparklineGradient={scenario?.sparklineGradient ?? false}
            showTrendColumn={scenario?.showTrendColumn !== false}
            showRowSparkline={scenario?.showRowSparkline !== false}
            semanticTone={!!scenario?.sparklineSemanticTone}
            guides={scenario?.guides}
            hideEmptyLines
            currentPeriodLabel={fccCurrentLabel}
            currentPeriodProof={fccCurrentProof}
          />
          <FormalStatementTable
            title="Projected Cash Flows"
            entity="Current Lens Entity"
            period={`${fccPeriodLabels[0]} – ${fccPeriodLabels[fccPeriodLabels.length - 1]}`}
            periods={fccPeriodLabels}
            sections={shapeCashFlowStatementMulti(fccCF)}
            sparklineGradient={scenario?.sparklineGradient ?? false}
            showTrendColumn={scenario?.showTrendColumn !== false}
            showRowSparkline={scenario?.showRowSparkline !== false}
            semanticTone={!!scenario?.sparklineSemanticTone}
            guides={scenario?.guides}
            hideEmptyLines
            currentPeriodLabel={fccCurrentLabel}
            currentPeriodProof={fccCurrentProof}
          />
        </div>
      </SectionCard>
      )}

      {/* Proposal 3 (2026-04-25) — Journal Entries are simulations and
          live in FCC alongside buy/sell and LTL sims. Engine still
          applies posted JEs to every module that consumes the income
          statement / balance sheet — this section just owns authoring. */}
      {show('overview', 'journal-entries') && features.journalEntries && (
        <SectionCard
          sectionId="journal-entries"
          title={`Journal Entries — ${adjEntries.length} total${adjEntries.length > 0 ? ` (${postedCount} posted · ${draftCount} draft)` : ''}`}
          subtitle="Post a balanced adjusting JE — flows into Actuals / Budgets / Forecasts statements automatically. Drafts stay private to this model state until posted."
          right={
            !showJEForm ? (
              <button
                type="button"
                className={PM_BTN_JE}
                onClick={() => setShowJEForm(true)}
              >
                + New entry
              </button>
            ) : null
          }
        >
          {showJEForm && (
            <div className="mb-3 px-[0.7rem] py-[0.5rem] bg-surface-el border border-accent-gold rounded-sm">
              <p className="text-[0.78rem] text-accent-gold m-0 mb-[0.5rem] uppercase tracking-[0.08em] font-semibold">
                ✦ New adjusting entry
              </p>
              <JournalEntryForm
                onPost={pushEntry}
                onSaveDraft={pushEntry}
                onCancel={() => setShowJEForm(false)}
              />
            </div>
          )}

          {adjEntries.length === 0 && !showJEForm ? (
            <div className="flex flex-col items-start gap-[0.6rem]">
              <p className={`${HINT_JE} mt-0`}>
                No adjusting entries yet. Post your first balanced JE — flows into Actuals / Budgets / Forecasts automatically.
              </p>
              <button
                type="button"
                className={PM_BTN_JE}
                onClick={() => setShowJEForm(true)}
              >
                + Post new entry →
              </button>
            </div>
          ) : adjEntries.length > 0 ? (
            <ul className="list-none p-0 m-0 flex flex-col gap-2">
              {[...adjEntries].reverse().map((e) => {
                const draftBorder = e.status === 'draft' ? 'border-dashed' : 'border-solid';
                return (
                  <li key={e.id} className={`px-[0.7rem] py-[0.55rem] rounded-sm bg-surface-el border border-border ${draftBorder}`}>
                    <div className="flex gap-2 items-baseline mb-[0.3rem] flex-wrap">
                      <strong className="text-accent-gold font-mono">{e.date}</strong>
                      <span className={e.status === 'posted' ? BADGE_POSTED : BADGE_STOPPED}>{e.status}</span>
                      <em className="text-text not-italic">{e.memo || '(no memo)'}</em>
                      {e.reverses && <span className={BADGE_FORECAST} title={`Reverses ${e.reverses}`}>REVERSE</span>}
                    </div>
                    <ul className="list-none p-0 m-0">
                      {e.lines.map((l, i) => (
                        <li key={i} className="grid grid-cols-[1fr_auto_auto] gap-3 px-[0.3rem] py-[0.18rem] text-[0.78rem] border-b border-dotted border-border last:border-b-0 tabular-nums">
                          <span>{l.account}</span>
                          <span className="text-accent-red text-right min-w-[7ch]">{Number(l.debit) > 0 ? fmtMoney(Number(l.debit), { decimals: 2 }) : ''}</span>
                          <span className="text-accent-green text-right min-w-[7ch]">{Number(l.credit) > 0 ? fmtMoney(Number(l.credit), { decimals: 2 }) : ''}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-[0.4rem] mt-[0.35rem]">
                      {e.status === 'posted' && !e.reverses && (
                        <button type="button" className={PM_BTN_JE} onClick={() => reverseEntry(e)}>Reverse</button>
                      )}
                      {e.status === 'draft' && (
                        <button type="button" className={PM_BTN_DANGER_JE} onClick={() => deleteDraft(e.id)}>Delete draft</button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </SectionCard>
      )}

      {/* v8 Path N — Central Simulations Registry.
          Aggregates every user-authored override across Actuals, Budgets,
          Forecasts, FCC, and Settings into one audit ledger. FCC owns the
          *asset transactions* + *journal entries* and can remove them in
          place (session-scoped); for every other module's simulation we
          surface a read-only entry with an "Edit in {module} →" deep link
          that bounces the user back to the owning module to edit. The
          discipline principle stays intact: each simulation lives where it
          belongs; FCC only shows the roll-up. */}
      {show('overview', 'transactions') && features.transactions && (
        <SimulationsRegistrySection
          scenario={scenario}
          txns={txns}
          onJump={onJump}
          removeTxn={removeTxn}
        />
      )}

      <AssetTransactionModal
        open={!!modal}
        mode={modal?.mode}
        asset={modal?.asset}
        ltl={modal?.ltl}
        onClose={() => setModal(null)}
        onApply={(txn) => {
          setScenario({ ...scenario, assetTransactions: [...(scenario.assetTransactions ?? []), txn] });
          setModal(null);
        }}
      />

      {/* Proposal 1 (2026-04-25) — module data inheritance. FCC is a
          sandbox; users need to see the ledger baseline they are
          mutating. Actuals context shows the "before" snapshot under
          every simulation overlay. */}
      {show('overview') && (
        <ContextPanel currentModule="fcc" contextModule="actuals" scenario={scenario} />
      )}
      {confirmDialog}
    </div>
  );
}

/* ============================================================
   v8 Path N — SimulationsRegistrySection
   ============================================================
   The central audit ledger of every scenario-level override, grouped by
   owning module. FCC entries (asset buy/sell/LTL) are editable in place
   (Remove button). Non-FCC entries are read-only — clicking "Edit in
   {Module} →" routes back to the owning module so the user can tune it
   there. This keeps the discipline rule: each simulation lives where it
   belongs; FCC only visualizes the stack.

   Visual hierarchy:
   - Empty state when there's *zero* registry content (not just zero txns)
   - One-line summary: "N simulations · X from Budgets · Y from FCC · …"
   - Grouped sections per module with module-colored left border */
const MODULE_TONE = {
  FCC:       { bar: 'border-l-accent-gold',  chip: 'bg-[rgba(240,180,41,0.12)] text-accent-gold border border-accent-gold/50' },
  Actuals:   { bar: 'border-l-accent-blue',  chip: 'bg-[rgba(96,165,250,0.14)] text-accent-blue border border-accent-blue/50' },
  Budgets:   { bar: 'border-l-accent-green', chip: 'bg-[rgba(74,222,128,0.14)] text-accent-green border border-accent-green/50' },
  Forecasts: { bar: 'border-l-accent-red',   chip: 'bg-[rgba(248,113,113,0.14)] text-accent-red border border-accent-red/50' },
  Settings:  { bar: 'border-l-border-strong',chip: 'bg-surface-el text-text-muted border border-border-strong' },
};
const REGISTRY_ROW = 'grid grid-cols-[auto_1fr_auto] gap-3 items-center px-[0.7rem] py-[0.55rem] bg-surface-el border-l-[3px] border-y border-r border-y-border border-r-border rounded-sm mb-[0.4rem]';
const REGISTRY_CHIP = 'text-[0.66rem] font-bold uppercase tracking-[0.06em] px-[6px] py-[1px] rounded-[3px] whitespace-nowrap';
const REGISTRY_BTN = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-[8px] py-[3px] rounded-sm font-[inherit] text-[0.72rem] cursor-pointer hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)] whitespace-nowrap';
const REGISTRY_BTN_DANGER = 'bg-transparent text-accent-red border border-dashed border-border-strong px-[8px] py-[3px] rounded-sm font-[inherit] text-[0.72rem] cursor-pointer hover:border-accent-red hover:bg-[rgba(248,113,113,0.08)] whitespace-nowrap';

function SimulationsRegistrySection({ scenario, txns, onJump, removeTxn }) {
  const registry = React.useMemo(() => buildSimulationsRegistry(scenario), [scenario]);
  const summary = React.useMemo(() => summarizeSimulationsRegistry(registry), [registry]);

  if (registry.length === 0) {
    return (
      <SectionCard
        sectionId="transactions"
        title="Simulated transactions"
        subtitle="Central registry of every model-state override across Actuals · Budgets · Forecasts · Command Module · Settings. None yet."
      >
        <EmptyState
          icon="↔"
          heading="No model-state simulations yet"
          body="This is the central audit ledger. Any override you set — buy/sell in Command Module, piecewise schedules in Budgets, multipliers in Forecasts, adjusting entries in Actuals, FX pins or account visibility in Settings — lands here with a back-link to its owning module. Start by editing something in any tab, or use the NC-Assets actions above."
          actions={[
            { label: 'Jump to NC-Assets', onClick: () => onJump && onJump({ tab: 'fcc', section: 'nc-assets-liabilities' }) },
            { label: 'Open Settings',        onClick: () => onJump && onJump({ tab: 'settings' }) },
          ]}
        />
      </SectionCard>
    );
  }

  // Group entries by module, preserving module display order
  const moduleOrder = ['FCC', 'Actuals', 'Budgets', 'Forecasts', 'Settings'];
  const grouped = moduleOrder
    .map(m => ({ module: m, entries: registry.filter(r => r.module === m) }))
    .filter(g => g.entries.length > 0);

  const summaryTail = moduleOrder
    .filter(m => summary.byModule[m])
    .map(m => `${summary.byModule[m]} from ${m}`)
    .join(' · ');

  return (
    <SectionCard
      sectionId="transactions"
      title={`Simulated transactions (${registry.length})`}
      subtitle={`Central audit ledger across all modules · ${summaryTail} · Read-only view — use "Edit in {module} →" to modify anything outside the Command Module. Save a Model Profile to keep them across reloads.`}
    >
      {grouped.map((g) => {
        const tone = MODULE_TONE[g.module] || MODULE_TONE.Settings;
        return (
          <div key={g.module} className="mb-[1rem] last:mb-0">
            <div className="flex items-center gap-2 mb-[0.4rem] px-[0.1rem]">
              <span className={`${REGISTRY_CHIP} ${tone.chip}`}>{g.module}</span>
              <span className="text-[0.72rem] text-text-dim">
                {g.entries.length} simulation{g.entries.length === 1 ? '' : 's'}
              </span>
            </div>
            {g.entries.map((e) => {
              const isFccTxn = e.module === 'FCC' && e.kind === 'asset-txn';
              const txnId = isFccTxn ? e.id.replace(/^fcc-txn-/, '') : null;
              return (
                <div key={e.id} className={`${REGISTRY_ROW} ${tone.bar}`}>
                  <span className={`${REGISTRY_CHIP} ${tone.chip}`}>{e.kind}</span>
                  <div className="min-w-0">
                    <div className="text-[0.85rem] text-text truncate">{e.label}</div>
                    <div className="text-[0.72rem] text-text-muted truncate">
                      {e.detail}
                      {e.impact && e.impact !== '—' && (
                        <> · <span className="text-text">{e.impact}</span></>
                      )}
                    </div>
                    <div className="text-[0.68rem] text-text-dim italic truncate mt-[1px]">
                      {e.reason}
                    </div>
                  </div>
                  <div className="flex items-center gap-[6px]">
                    {isFccTxn && txnId && (
                      <button
                        type="button"
                        className={REGISTRY_BTN_DANGER}
                        onClick={() => removeTxn(txnId)}
                        title="Remove this Command Module-owned simulation (session-scoped)"
                      >
                        Remove
                      </button>
                    )}
                    <button
                      type="button"
                      className={REGISTRY_BTN}
                      onClick={() => {
                        if (!onJump || !e.editRoute) return;
                        const [tab, section] = e.editRoute.split(':');
                        onJump({ tab, section });
                      }}
                      title={`Edit this simulation in ${e.module}`}
                    >
                      Edit in {e.module} →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      <p className="text-[0.7rem] text-text-dim italic mt-2 mb-0">
        Each simulation lives in its owning module's authoring view. This ledger is the read-only union — clicking "Edit in {'{module}'} →" routes you back to the source. Session-scoped unless you save a Model Profile.
      </p>
    </SectionCard>
  );
}
